import { useEffect, useMemo, useState } from "react";
import { useUiStore } from "@/stores/ui";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import type { Account, Backtest, Position } from "@/api/types";
import { useApi } from "../context/ApiContext";
import { ActionsMenu } from "../components/ActionsMenu";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { Search } from "lucide-react";
import { Card, ExchangeMark, accountPill, statusPill } from "../components/ui/kit";
import { Pager } from "@/components/ui/Pager";
import { clampPage } from "@/lib/paging";
import { FilterBar } from "@/components/ui/FilterDialog";
import { accountUsdBalance } from "@/lib/account";
import { toDateTime } from "@/lib/backtest-run";
import { defaultStartDate, isLiveInstance, marketLabel, scriptLabel } from "@/lib/live";
import { realPnlKey, sumRealPnl, useLiveRealPnl, type RealPnl } from "@/hooks/useLiveRealPnl";

const money = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });
const signed = (n: number) => `${n >= 0 ? "+" : "−"}$${money.format(Math.abs(n))}`;
const PAGE_SIZE = 12;

type Filter = "all" | "running" | "stopped" | "error";
const FILTERS: { id: Filter; label: string }[] = [
  { id: "all", label: "All" },
  { id: "running", label: "Running" },
  { id: "stopped", label: "Stopped" },
  { id: "error", label: "Errors" },
];
function filterOf(bt: Backtest): Filter {
  if (!bt.enabled || bt.status === "stopped") return "stopped";
  if (bt.status === "error") return "error";
  return "running";
}

const stat = "text-[11px] uppercase tracking-[0.06em] text-[var(--text-muted)]";
const statValue = "num mt-0.5 text-[17px] font-semibold text-[var(--text-primary)]";

/** Capital the strategy trades with (its engine's starting balance). */
function capitalOf(bt: Backtest): number | null {
  const cfg = (bt.engine_config || {}) as Record<string, unknown>;
  const n = Number(cfg.initial ?? cfg.fiat);
  return Number.isFinite(n) && n > 0 ? n : null;
}

const sym = (s?: string) => (s || "").toUpperCase().replace(/\.P$/, "");

/**
 * One live strategy on one exchange account: what it trades, how it's doing there,
 * and Start/Stop at hand. A strategy on two accounts has a card under each.
 */
function LiveCard({
  bt,
  accountId,
  alsoOn,
  pnl,
  position,
  busy,
  onToggle,
  onDelete,
}: {
  bt: Backtest;
  accountId: string | null;
  /** The strategy's other accounts, by name. */
  alsoOn: string[];
  pnl: RealPnl | undefined;
  /** The exchange's open position on this strategy's symbol, if any. */
  position: Position | undefined;
  busy: boolean;
  onToggle: (bt: Backtest, enabled: boolean) => void;
  onDelete: (bt: Backtest) => void;
}) {
  const cap = capitalOf(bt);
  const to = `/live/${bt.id}${alsoOn.length && accountId ? `?account=${encodeURIComponent(accountId)}` : ""}`;
  const upnl = position ? Number(position.unrealized_pnl) : NaN;
  return (
    <section
      aria-label={bt.name}
      className="flex min-w-0 flex-col gap-4 rounded-card border border-[var(--border)] bg-[var(--bg-card)] p-4"
    >
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <Link
            to={to}
            className="block truncate text-[15px] font-semibold text-[var(--text-primary)] no-underline hover:underline"
          >
            {bt.name}
          </Link>
          <div className="truncate text-[13px] text-[var(--text-muted)]">{marketLabel(bt)}</div>
          {alsoOn.length > 0 && (
            <div className="truncate text-xs text-[var(--text-muted)]" title="Start, Stop and Delete apply on every account">
              Also on {alsoOn.join(", ")}
            </div>
          )}
        </div>
        <span className="shrink-0">{statusPill(bt.status, bt.enabled)}</span>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <div title="Realized from orders filled on the exchange">
          <div className={stat}>Real P&amp;L</div>
          <div
            className={statValue}
            style={pnl?.trades ? { color: pnl.pnl >= 0 ? "var(--success)" : "var(--danger)" } : { color: "var(--text-muted)" }}
          >
            {pnl ? (pnl.trades ? signed(pnl.pnl) : "—") : "…"}
          </div>
        </div>
        <div title="Share of closed trades that made money">
          <div className={stat}>Win rate</div>
          <div className={statValue}>
            {pnl?.trades ? `${Math.round(pnl.winRate * 100)}%` : <span className="text-[var(--text-muted)]">—</span>}
          </div>
          {pnl?.trades ? (
            <div className="num text-xs text-[var(--text-muted)]">
              {Math.round(pnl.winRate * pnl.trades)}/{pnl.trades} won
            </div>
          ) : pnl ? (
            <div className="text-xs text-[var(--text-muted)]">No trades yet</div>
          ) : null}
        </div>
        <div>
          <div className={stat}>Capital</div>
          <div className={statValue}>{cap ? `$${money.format(cap)}` : "—"}</div>
        </div>
      </div>

      {position && (
        <div className="-mt-1 flex items-center gap-2 rounded-[10px] bg-[var(--bg-hover)] px-2.5 py-1.5 text-[13px]">
          <span className="font-medium text-[var(--text-primary)]">{position.side === "SHORT" ? "Short" : "Long"} open</span>
          <span className="num text-[var(--text-muted)]">{position.size}</span>
          {Number.isFinite(upnl) && (
            <span className="num ml-auto" style={{ color: upnl >= 0 ? "var(--success)" : "var(--danger)" }}>
              {signed(upnl)} <span className="text-xs text-[var(--text-muted)]">unrealized</span>
            </span>
          )}
        </div>
      )}

      <div className="mt-auto flex items-center gap-2">
        <Link to={to} className="btn btn-primary btn-sm flex-1">
          Open
        </Link>
        {bt.enabled ? (
          <button type="button" className="btn btn-soft btn-sm flex-1" disabled={busy} onClick={() => onToggle(bt, false)}>
            Stop
          </button>
        ) : (
          <button type="button" className="btn btn-soft btn-sm flex-1" disabled={busy} onClick={() => onToggle(bt, true)}>
            Start
          </button>
        )}
        <ActionsMenu items={[{ label: "Delete", danger: true, onClick: () => onDelete(bt) }]} />
      </div>
    </section>
  );
}

function ExchangeGroup({
  account,
  rows,
  busyId,
  onToggle,
  onDelete,
  real,
}: {
  account: Account | null;
  rows: Backtest[];
  busyId: string | null;
  onToggle: (bt: Backtest, enabled: boolean) => void;
  onDelete: (bt: Backtest) => void;
  /** Realized P&L per strategy from exchange orders. */
  real: Record<string, RealPnl>;
}) {
  const api = useApi(["accounts", "backtests", "scripts"]);
  const balance = account ? accountUsdBalance(api.balancesByAccount?.[account.id]) : null;
  const positions = account ? api.positionsByAccount?.[account.id] : undefined;
  const inUse = rows.filter((r) => r.enabled).reduce((sum, r) => sum + (capitalOf(r) || 0), 0);
  const total = balance ? Math.max(balance.value, inUse) : 0;

  return (
    <section aria-label={account?.name || "No exchange"} className="flex min-w-0 flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2.5">
        {account ? <ExchangeMark provider={account.provider} /> : null}
        <b className="text-sm font-semibold text-[var(--text-primary)]">
          {account ? (
            <Link to={`/integrations/marketplaces/${account.id}`} className="text-inherit no-underline hover:underline">
              {account.name}
            </Link>
          ) : (
            "No exchange"
          )}
        </b>
        {account && account.status !== "running" && accountPill(account)}
        {balance && (
          <>
            <span className="relative ml-auto h-1 w-[120px] overflow-hidden rounded bg-[var(--bg-hover)]" aria-hidden>
              <i className="absolute inset-y-0 left-0 rounded bg-[var(--primary)]" style={{ width: `${total ? Math.min(100, (inUse / total) * 100) : 0}%` }} />
            </span>
            <span className="num text-xs text-[var(--text-muted)]">
              {money.format(inUse)} of {money.format(total)} {balance.asset} in use
            </span>
          </>
        )}
      </div>
      <div className="grid gap-4 [grid-template-columns:repeat(auto-fill,minmax(300px,1fr))]">
        {rows.map((bt) => (
          <LiveCard
            key={bt.id}
            bt={bt}
            accountId={account?.id ?? null}
            alsoOn={(bt.accounts || [])
              .filter((a) => a.account_id !== account?.id)
              .map((a) => api.accounts?.find((acc: Account) => acc.id === a.account_id)?.name || "another account")}
            pnl={real[realPnlKey(bt.id, account?.id)]}
            position={positions?.find((p) => sym(p.symbol) === sym(bt.symbol))}
            busy={busyId === bt.id}
            onToggle={onToggle}
            onDelete={onDelete}
          />
        ))}
      </div>
    </section>
  );
}

export default function LiveList() {
  const api = useApi(["accounts", "backtests", "scripts"]);
  const [params, setParams] = useSearchParams();
  const openGoLive = useUiStore((st) => st.openGoLive);
  const [q, setQ] = useState(() => params.get("q") ?? "");
  // Global search links here with ?q=SYMBOL.
  useEffect(() => {
    setQ(params.get("q") ?? "");
  }, [params]);
  const [filter, setFilter] = useState<Filter>("all");
  const [exchange, setExchange] = useState("");
  const [page, setPage] = useState(0);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Backtest | null>(null);

  const accounts: Account[] = useMemo(() => api.accounts || [], [api.accounts]);
  const live: Backtest[] = useMemo(() => (api.backtests || []).filter(isLiveInstance), [api.backtests]);
  // A card per strategy per account it trades on, in the order accounts are listed,
  // unlinked last, so a page's cards group by exchange.
  const cards = useMemo(() => {
    const out: { account: Account | null; bt: Backtest }[] = [];
    for (const account of accounts)
      for (const bt of live) if (bt.accounts?.some((a) => a.account_id === account.id)) out.push({ account, bt });
    for (const bt of live)
      if (!bt.accounts?.some((a) => accounts.some((acc) => acc.id === a.account_id))) out.push({ account: null, bt });
    return out;
  }, [accounts, live]);
  const counts = useMemo(() => {
    const out: Record<Filter, number> = { all: cards.length, running: 0, stopped: 0, error: 0 };
    for (const { bt } of cards) out[filterOf(bt)]++;
    return out;
  }, [cards]);
  const ordered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return cards.filter(
      ({ account, bt }) =>
        (filter === "all" || filterOf(bt) === filter) &&
        (!exchange || (account?.id ?? "none") === exchange) &&
        (!needle ||
          bt.name?.toLowerCase().includes(needle) ||
          bt.symbol?.toLowerCase().includes(needle) ||
          account?.name.toLowerCase().includes(needle) ||
          scriptLabel(bt, api.scripts).toLowerCase().includes(needle))
    );
  }, [cards, q, filter, exchange, api.scripts]);
  // The accounts that have a card, for the exchange filter.
  const exchanges = useMemo(() => {
    const seen = new Map<string, string>();
    for (const { account } of cards) seen.set(account?.id ?? "none", account?.name ?? "No exchange");
    return [...seen];
  }, [cards]);
  const shownPage = clampPage(page, PAGE_SIZE, ordered.length);
  const groups = useMemo(() => {
    const out: { account: Account | null; rows: Backtest[] }[] = [];
    for (const { account, bt } of ordered.slice(shownPage * PAGE_SIZE, (shownPage + 1) * PAGE_SIZE)) {
      const last = out[out.length - 1];
      if (last && last.account?.id === account?.id) last.rows.push(bt);
      else out.push({ account, rows: [bt] });
    }
    return out;
  }, [ordered, shownPage]);

  const real = useLiveRealPnl(live);
  // Every live strategy on every account, whatever the filters show.
  const total = useMemo(() => sumRealPnl(real), [real]);

  async function toggle(bt: Backtest, enabled: boolean) {
    setBusyId(bt.id);
    setError(null);
    try {
      await api.commands.updateBacktest(enabled
          ? { id: bt.id, start_date: toDateTime(bt.start_date) || defaultStartDate(bt), end_date: null, enabled: true }
          : { id: bt.id, enabled: false }
      );
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Couldn't update the strategy");
    } finally {
      setBusyId(null);
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    setBusyId(deleteTarget.id);
    setError(null);
    try {
      await api.commands.deleteBacktest({ id: deleteTarget.id });
      setDeleteTarget(null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Delete failed");
    } finally {
      setBusyId(null);
    }
  }

  // Trades moved to their own page; old links still land there.
  if (params.get("tab") === "trades") return <Navigate to="/trades" replace />;

  return (
    <div className="flex min-w-0 flex-col gap-5">
      {/* No page title: the menu says where you are, so the totals and tools share one row: search, then filters. */}
      <h1 className="sr-only">Live</h1>
      <div className="flex flex-wrap items-center justify-between gap-2.5">
        {live.length > 0 && (
          <div
            role="group"
            aria-label="Totals from live trading"
            title="All live strategies on every account, from orders filled on the exchange, without fees and funding"
            className="flex items-center gap-5 rounded-card border border-[var(--border)] bg-[var(--bg-card)] px-4 py-2"
          >
            <div>
              <div className="text-[10px] uppercase tracking-[0.06em] text-[var(--text-muted)]">Total real P&amp;L</div>
              <div
                className="num text-[18px] font-semibold"
                style={{ color: total?.trades ? (total.pnl >= 0 ? "var(--success)" : "var(--danger)") : "var(--text-muted)" }}
              >
                {total ? (total.trades ? signed(total.pnl) : "—") : "…"}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.06em] text-[var(--text-muted)]">Win rate</div>
              <div className="num text-[18px] font-semibold text-[var(--text-primary)]">
                {total?.trades ? `${Math.round(total.winRate * 100)}%` : <span className="text-[var(--text-muted)]">—</span>}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.06em] text-[var(--text-muted)]">Trades</div>
              <div className="num text-[18px] font-semibold text-[var(--text-primary)]">
                {total ? total.trades.toLocaleString() : <span className="text-[var(--text-muted)]">…</span>}
              </div>
            </div>
          </div>
        )}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {live.length > 0 && (
            <>
            <label className="flex min-w-[220px] items-center gap-2 rounded-[10px] border border-[var(--border)] bg-[var(--bg-secondary)] px-2.5 py-1.5 text-[13px] text-[var(--text-disabled)] focus-within:border-[var(--primary)]">
              <Search size={14} className="shrink-0" />
              <input
                type="search"
                aria-label="Search live strategies"
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  setPage(0);
                }}
                placeholder="Search strategies"
                className="!h-auto !min-h-0 w-full !border-0 !bg-transparent !p-0 !text-[13px] text-[var(--text-primary)] !shadow-none !outline-none placeholder:text-[var(--text-disabled)]"
              />
            </label>
            <FilterBar
              title="Filter live strategies"
              shown={ordered.length}
              groups={[
                {
                  id: "status",
                  label: "Status",
                  value: filter,
                  defaultValue: "all",
                  options: FILTERS.map((f) => ({ value: f.id, label: f.label, count: counts[f.id] })),
                },
                ...(exchanges.length > 1
                  ? [
                      {
                        id: "exchange",
                        label: "Exchange",
                        value: exchange,
                        defaultValue: "",
                        options: [
                          { value: "", label: "All exchanges" },
                          ...exchanges.map(([id, name]) => ({
                            value: id,
                            label: name,
                            count: cards.filter((c) => (c.account?.id ?? "none") === id).length,
                          })),
                        ],
                      },
                    ]
                  : []),
              ]}
              onChange={(id, value) => {
                if (id === "status") setFilter(value as Filter);
                else setExchange(value);
                setPage(0);
              }}
              onClear={() => {
                setFilter("all");
                setExchange("");
                setPage(0);
              }}
            />
            </>
          )}
          <button type="button" className="btn btn-primary" onClick={openGoLive}>
            + Go live with a backtest
          </button>
        </div>
      </div>

        {error && <div className="rounded-[10px] bg-[var(--danger-bg)] px-3 py-2 text-[13px] text-[var(--danger)]">{error}</div>}

        {!api.ready ? (
          <Card className="py-8 text-center text-sm text-[var(--text-muted)]">Loading…</Card>
        ) : live.length === 0 ? (
          <Card className="flex flex-col items-center gap-3 px-4 py-12 text-center">
            <b className="text-[15px] text-[var(--text-primary)]">Nothing live yet</b>
            <p className="m-0 max-w-[420px] text-[13px] text-[var(--text-muted)]">
              Pick a backtest you're happy with and run it on one of your exchanges.
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              <button type="button" className="btn btn-primary" onClick={openGoLive}>
                Go live with a backtest
              </button>
              {accounts.length === 0 && (
                <Link to="/integrations/marketplaces/new" className="btn btn-ghost">
                  Add an exchange account first
                </Link>
              )}
            </div>
          </Card>
        ) : groups.length === 0 ? (
          <div className="flex flex-col items-center gap-2.5 px-4 py-10 text-center text-[13px] text-[var(--text-muted)]">
            Nothing matches these filters.
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => {
                setQ("");
                setFilter("all");
                setExchange("");
                setParams({});
              }}
            >
              Clear filters
            </button>
          </div>
        ) : (
          groups.map((g, i) => (
            <ExchangeGroup
              key={`${g.account?.id || "none"}-${i}`}
              account={g.account}
              rows={g.rows}
              busyId={busyId}
              onToggle={(bt, enabled) => void toggle(bt, enabled)}
              onDelete={setDeleteTarget}
              real={real}
            />
          ))
        )}
        <Pager page={shownPage} pageSize={PAGE_SIZE} total={ordered.length} onPage={setPage} className="" />
      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title={`Delete "${deleteTarget?.name}"?`}
        description="The live strategy stops and is removed."
        loading={busyId === deleteTarget?.id}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => void handleDelete()}
      />
    </div>
  );
}
